import * as React from 'react';
import { ROUTES } from '@/common/auth-routes';
import { ValidationStatus } from '@/features/onboarding/hooks/use-token-validation';
import type { TokenValidationResult } from '@/features/onboarding/types/onboarding.types';
import { OnboardingPageAlreadyCompleted } from './onboarding-page-already-completed';
import { OnboardingPageError } from './onboarding-page-error';
import { OnboardingPageExpired } from './onboarding-page-expired';
import { OnboardingPageLoading } from './onboarding-page-loading';

interface OnboardingTokenValidationStatusScreenProps {
  status: Exclude<ValidationStatus, ValidationStatus.VALID>;
  validationResult: TokenValidationResult | null;
  error: string | null;
  /** Builds the sign-in URL given a tenant slug (differs per page context) */
  buildSignInUrl: (tenantSlug: string) => string;
}

/**
 * Renders the appropriate full-page state for each non-Valid validation status.
 */
export function OnboardingTokenValidationStatusScreen({
  status,
  validationResult,
  error,
  buildSignInUrl,
}: OnboardingTokenValidationStatusScreenProps): React.ReactElement {
  switch (status) {
    case ValidationStatus.VALIDATING:
      return <OnboardingPageLoading />;
    case ValidationStatus.ALREADY_COMPLETED: {
      const tenantSlug = validationResult?.tenant_slug;

      return (
        <OnboardingPageAlreadyCompleted
          signInUrl={
            tenantSlug ? buildSignInUrl(tenantSlug) : ROUTES.TENANT_SELECTION
          }
          userEmail={validationResult?.user_email}
        />
      );
    }
    case ValidationStatus.EXPIRED:
      return <OnboardingPageExpired />;
    case ValidationStatus.INVALID:
      return <OnboardingPageError error={error} />;
    default: {
      const _exhaustive: never = status;

      return _exhaustive;
    }
  }
}
